import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import "react-dates/initialize";
import { DateRangePicker, SingleDatePicker } from "react-dates";
import "react-dates/lib/css/_datepicker.css";
import { API_DATE_FORMAT } from "../../app_constants";
import { useLightTheme } from "../../hooks/useLightTheme";

const DEFAULT_DAY_SIZE = 39;
const LIGHT_THEME_DAY_SIZE = 28;
const DISABLED_NAV_CLASSES = [
  "DayPickerNavigation_button",
  "DayPickerNavigation_button__default",
  "DayPickerNavigation_button__horizontalDefault",
  "DayPickerNavigation_button__disabled"
].join(" ");

const ReactDatePicker = ({
  startDate,
  endDate,
  minDate,
  maxDate,
  updateDates,
  isRangePicker,
  calendarEndDate,
  numberOfMonths,
  firstDataDate,
  showDefaultInputIcon,
  disabled = false,
  showClearDate = false,
  allowAllDays = false,
  displayFormat = "MM/DD/YYYY"
}) => {
  const rootRef = useRef(null);
  const [isAdminLight, setIsAdminLight] = useState(false);
  //TODO: Remove after updating the dark theme
  useEffect(() => setIsAdminLight(!!rootRef.current.closest(".light")), []);

  const isLightTheme = useLightTheme() || isAdminLight;
  const daySize = isLightTheme ? LIGHT_THEME_DAY_SIZE : DEFAULT_DAY_SIZE;

  const startDateState = startDate ? moment(startDate) : null;

  const [minBound, maxBound] = useMemo(() => {
    if (allowAllDays) return [null, null];

    const max = moment(calendarEndDate);
    if (minDate !== undefined) return [moment(minDate), max];
    if (numberOfMonths !== undefined) {
      return [moment(calendarEndDate).subtract(numberOfMonths, "month"), max];
    }
    if (firstDataDate !== undefined) return [moment(firstDataDate), max];

    return [null, max];
  }, [allowAllDays, minDate, numberOfMonths, firstDataDate, calendarEndDate]);

  const lastNavMonth = maxBound && moment(maxBound).subtract(1, "month");

  const visibleMonthFor = (date) => {
    const month = moment(date);
    if (!lastNavMonth || month.isSameOrBefore(lastNavMonth, "month")) return month;

    return minBound && lastNavMonth.isBefore(minBound, "month") ? month : moment(lastNavMonth);
  };

  const [endDateState, setEndDateState] = useState(moment(endDate));
  const [focusedInput, setFocusedInput] = useState(null);
  const [focused, setFocused] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() =>
    visibleMonthFor(isRangePicker ? endDate : startDateState || moment())
  );

  const disablePrevNav = minBound && visibleMonth.isSameOrBefore(minBound, "month");
  const disableNextNav = lastNavMonth && visibleMonth.isSameOrAfter(lastNavMonth, "month");

  useEffect(() => {
    setEndDateState(moment(endDate));
  }, [endDate]);

  const dateChange = (startDate, endDate) => {
    if (startDate !== null && endDate !== null) {
      updateDates(
        moment(startDate).format(API_DATE_FORMAT),
        moment(endDate).format(API_DATE_FORMAT)
      );
    }

    if (!isRangePicker && showClearDate && startDate == null) {
      updateDates(null);
    }
  };

  const renderCalendarInfo = () => {
    let formattedMinDate = "";
    let formattedMaxDate = maxDate ? maxDate : calendarEndDate;
    if (numberOfMonths) {
      formattedMinDate = moment(calendarEndDate).subtract(numberOfMonths, "month");
    } else if (minDate) {
      formattedMinDate = moment(minDate);
    } else if (firstDataDate) {
      formattedMinDate = moment(firstDataDate);
    }

    formattedMinDate = formattedMinDate && formattedMinDate.format(displayFormat);
    formattedMaxDate = moment(formattedMaxDate).format(displayFormat);
    return minDate ? (
      <div className="date-picker-info">
        <span className="glyphicon glyphicon-info-sign" />
        <span className="date-picker-info__label"> Date should be in range: </span>
        <span className="date-picker-info__range">
          {`${formattedMinDate} - ${formattedMaxDate}`}
        </span>
      </div>
    ) : (
      ""
    );
  };

  const doFocusChange = (type) => {
    if (type === "endDate") {
      setVisibleMonth(visibleMonthFor(endDate));
    } else if (type === "startDate") {
      setVisibleMonth(visibleMonthFor(startDate));
    }
  };

  const isOutsideRange = (day) =>
    Boolean(
      (minBound && day.isBefore(minBound, "day")) || (maxBound && day.isAfter(maxBound, "day"))
    );

  const disabledNav = (side) => (
    <div
      className={`${DISABLED_NAV_CLASSES} DayPickerNavigation_${side}Button__horizontalDefault`}
      aria-disabled
    />
  );

  return (
    <div ref={rootRef}>
      {isRangePicker ? (
        <DateRangePicker
          calendarInfoPosition="top"
          renderCalendarInfo={renderCalendarInfo}
          daySize={daySize}
          startDate={startDateState}
          startDateId="your_unique_start_date_id"
          endDate={endDateState}
          endDateId="your_unique_end_date_id"
          onDatesChange={({ startDate, endDate }) => dateChange(startDate, endDate)}
          focusedInput={focusedInput}
          onFocusChange={(focusedInput) => {
            doFocusChange(focusedInput), setFocusedInput(null);
            // hack to re-render the calendar at correct month based on date click.
            setTimeout(() => {
              setFocusedInput(focusedInput);
            }, 1);
          }}
          initialVisibleMonth={() => visibleMonth}
          showDefaultInputIcon={showDefaultInputIcon}
          isOutsideRange={isOutsideRange}
          minDate={minBound}
          maxDate={maxBound}
          minimumNights={0}
          disabled={disabled}
          displayFormat={displayFormat}
        />
      ) : (
        <SingleDatePicker
          displayFormat={displayFormat}
          calendarInfoPosition="top"
          renderCalendarInfo={renderCalendarInfo}
          daySize={daySize}
          date={startDateState}
          onDateChange={(date) => dateChange(date, date)}
          focused={focused}
          onFocusChange={({ focused }) => setFocused(focused)}
          initialVisibleMonth={() => visibleMonth}
          onPrevMonthClick={setVisibleMonth}
          onNextMonthClick={setVisibleMonth}
          renderNavPrevButton={
            isLightTheme && disablePrevNav ? () => disabledNav("left") : undefined
          }
          renderNavNextButton={
            isLightTheme && disableNextNav ? () => disabledNav("right") : undefined
          }
          id="your_unique_id"
          showDefaultInputIcon={showDefaultInputIcon ?? true}
          isOutsideRange={isOutsideRange}
          disabled={disabled}
          showClearDate={showClearDate}
        />
      )}
    </div>
  );
};

export default React.memo(ReactDatePicker);
